import React, { useCallback, useEffect, useState } from "react";
import PageHeader from "../components/PageHeader";
import { fetchDealerMessages, sendDealerMessage } from "../services/api";
import "../styles/DealerMessagesPage.css";

const MAX_ATTACHMENT_COUNT = 4;
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const ACCEPTED_FILES = "image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt";

function formatMessageTime(value) {
  if (!value) return "";
  return new Date(value).toLocaleString("en-IN", {
    day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

function fileAsAttachment(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({
      name: file.name,
      type: file.type || "application/octet-stream",
      size: file.size,
      dataUrl: reader.result,
    });
    reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
    reader.readAsDataURL(file);
  });
}

function MessageAttachments({ attachments = [] }) {
  if (!attachments.length) return null;
  return (
    <div className="dealer-message-attachments">
      {attachments.map((attachment) => (
        attachment.type?.startsWith("image/") ? (
          <a key={attachment.id || attachment.name} href={attachment.dataUrl} target="_blank" rel="noreferrer">
            <img src={attachment.dataUrl} alt={attachment.name} />
          </a>
        ) : (
          <a key={attachment.id || attachment.name} href={attachment.dataUrl} download={attachment.name}>
            📎 {attachment.name}
          </a>
        )
      ))}
    </div>
  );
}

function DealerMessagesPage() {
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const data = await fetchDealerMessages();
      setThread(data.thread || null);
      setMessages(data.messages || []);
      setError("");
    } catch (err) {
      setError(err.message || "Unable to load messages.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 30000);
    return () => window.clearInterval(timer);
  }, [load]);

  const chooseFiles = (event) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    if (selected.length > MAX_ATTACHMENT_COUNT) {
      setError("Attach up to 4 files per message.");
      return;
    }
    if (selected.reduce((sum, file) => sum + file.size, 0) > MAX_ATTACHMENT_BYTES) {
      setError("Attachments must total 5 MB or less.");
      return;
    }
    setError("");
    setFiles(selected);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!body.trim() && !files.length) return;
    try {
      setSending(true);
      setError("");
      const attachments = await Promise.all(files.map(fileAsAttachment));
      await sendDealerMessage(body.trim(), attachments);
      setBody("");
      setFiles([]);
      await load();
    } catch (err) {
      setError(err.message || "Unable to send message.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="dealer-messages-page">
      <PageHeader title="Messages" />
      <main className="dealer-messages-card">
        <div className="dealer-messages-intro">
          <h2>Contact ABN Textiles</h2>
          <p>Your message is shared with your assigned salesperson and dispatch team.</p>
        </div>
        {error && <p className="dealer-messages-alert" role="alert">{error}</p>}
        {loading ? <p className="dealer-messages-empty">Loading messages…</p> : (
          <>
            <div className="dealer-messages-history" aria-live="polite">
              {messages.length ? messages.map((message) => (
                <article className={message.sender_type === "dealer" ? "dealer-message dealer-message-own" : "dealer-message"} key={message.id}>
                  <div className="dealer-message-meta">
                    <strong>{message.sender_name}</strong>
                    <time>{formatMessageTime(message.created_at)}</time>
                  </div>
                  {message.body !== "Shared an attachment" && <p>{message.body}</p>}
                  <MessageAttachments attachments={message.attachments || []} />
                </article>
              )) : (
                <p className="dealer-messages-empty">
                  {thread ? "No messages yet." : "Send a message to start a conversation."}
                </p>
              )}
            </div>
            <form className="dealer-messages-compose" onSubmit={submit}>
              <div className="dealer-message-compose-main">
                <textarea value={body} onChange={(event) => setBody(event.target.value)} maxLength={4000} rows={3} placeholder="Write your message…" aria-label="Message" required={!files.length} />
                {files.length > 0 && (
                  <ul className="dealer-message-file-list">
                    {files.map((file) => <li key={file.name}>{file.name} <span>({(file.size / 1024).toFixed(0)} KB)</span></li>)}
                  </ul>
                )}
                <label className="dealer-message-attach">
                  <input type="file" accept={ACCEPTED_FILES} multiple onChange={chooseFiles} />
                  <span>Attach files</span>
                  <small>Up to 4 files, 5 MB total</small>
                </label>
              </div>
              <button type="submit" disabled={sending || (!body.trim() && !files.length)}>{sending ? "Sending…" : "Send"}</button>
            </form>
          </>
        )}
      </main>
    </div>
  );
}

export default DealerMessagesPage;
